import React from 'react'
import { makeStyles } from '@material-ui/core/styles'
import ListItem from '@material-ui/core/ListItem'
import ListItemIcon from '@material-ui/core/ListItemIcon'
import ListItemText from '@material-ui/core/ListItemText'
import Collapse from '@material-ui/core/Collapse'
import Icon from 'components/Icon'
import ExpandLess from '@material-ui/icons/ExpandLess'
import ExpandMore from '@material-ui/icons/ExpandMore'
import { Tooltip, Box } from '@material-ui/core'
import { useSelector } from 'react-redux'
import { IRootReduxState } from 'types/reducers'
import clsx from 'clsx'
import { PUBLIC_SANS_FONT_FAMILY, SIDEBAR_MENU_FONT_WEIGHT } from 'etc/appFont'

const useStyles = makeStyles((theme) => ({
    /** Expanded drawer: text-only submenus (icons only in collapsed rail via nestedCollapsedDrawer) */
    nested: {
        paddingLeft: theme.spacing(10.5),
        position: 'relative',
        '&::before': {
            display: 'none',
            content: '""',
            position: 'absolute',
            left: theme.spacing(4.5),
            top: 0,
            bottom: 0,
            width: '1px',
            backgroundColor: theme.palette.text.secondary,
        },
        '& .MuiListItem-root': {
            position: 'relative',
            paddingLeft: '24px',//theme.spacing(2),
            '&::before': {
                display: 'none',
                content: '"→"',
                position: 'absolute',
                left: '-13px',
                color: theme.palette.text.secondary,
            },
            '& .MuiListItemIcon-root': {
                display: 'none',
            },
        },
    },
    /** Narrow drawer: stack submenu icons without extra indent; icons slightly smaller than top-level */
    nestedCollapsedDrawer: {
        paddingLeft: 0,
        paddingTop: 0,
        paddingBottom: 0,
        margin: 0,
        '&::before': {
            display: 'none',
        },
        '& .MuiList-root': {
            paddingTop: 0,
            paddingBottom: 0,
        },
        '& .MuiListItem-root': {
            '&::before': {
                display: 'none',
            },
        },
        /* Submenu row with icon: tight vertical rhythm to match top-level nav */
        '& .MuiListItem-root:has(.MuiListItemIcon-root)': {
            alignItems: 'center',
            minHeight: 36,
            paddingTop: 2,
            paddingBottom: 2,
            paddingLeft: theme.spacing(0.5),
            paddingRight: theme.spacing(0.5),
            '& .MuiListItemIcon-root': {
                display: 'flex',
                minWidth: `${theme.spacing(4.5)}px`,
                marginRight: 0,
                justifyContent: 'center',
                color: 'inherit',
                '& svg, & .MuiSvgIcon-root': {
                    fontSize: '1.05rem',
                    width: '1em',
                    height: '1em',
                },
            },
            '& .MuiListItemText-root': {
                display: 'none',
            },
        },
        /* Submenu row without icon: keep a compact label (avoids empty rows until icons are added) */
        '& .MuiListItem-root:not(:has(.MuiListItemIcon-root))': {
            minHeight: 36,
            paddingTop: 2,
            paddingBottom: 2,
            paddingLeft: theme.spacing(0.5),
            paddingRight: theme.spacing(0.5),
            alignItems: 'center',
            '& .MuiListItemText-root': {
                marginTop: 0,
                marginBottom: 0,
                flex: '1 1 auto',
                minWidth: 0,
                textAlign: 'center',
            },
            '& .MuiListItemText-primary': {
                fontSize: '0.65rem',
                lineHeight: 1.15,
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                whiteSpace: 'nowrap',
            },
        },
    },
    /** Remove default Collapse inner spacing so submenu sits flush under parent icon */
    collapseDrawerCollapsed: {
        margin: 0,
        '& .MuiCollapse-wrapper': {
            marginTop: 0,
            marginBottom: 0,
        },
        '& .MuiCollapse-wrapperInner': {
            paddingTop: 0,
            paddingBottom: 0,
            margin: 0,
        },
    },
    /** Match MenuListItem row layout (same as plain links; drawer styles handle icon width). */
    parentListItem: {
        flexDirection: 'row',
        alignItems: 'center',
        width: '100%',
        boxSizing: 'border-box',
    },
    /** Same SVG sizing as MenuListItem (ListItemIcon must be direct child of ListItem) */
    parentMenuIcon: {
        '& svg': {
            fill: 'currentColor',
            width: '1em',
            height: '1em',
            display: 'inline-block',
            fontSize: '1.25rem',
            transition: 'fill 200ms cubic-bezier(0.4, 0, 0.2, 1) 0ms',
            flexShrink: 0,
            userSelect: 'none',
        },
    },
    parentListItemTextRoot: {
        flex: '1 1 auto',
        minWidth: 0,
    },
    menuListItem: {
        paddingTop: '5px',
        paddingBottom: '5px',
        fontFamily: PUBLIC_SANS_FONT_FAMILY,
        '& .MuiListItemText-primary': {
            fontFamily: PUBLIC_SANS_FONT_FAMILY,
            fontWeight: SIDEBAR_MENU_FONT_WEIGHT,
        },
        '&:hover': {
            backgroundColor: 'rgba(9, 140, 207, 0.27)',
        },
    },
    expandIcon: {
        marginLeft: 'auto',
        display: 'flex',
        alignItems: 'center',
        flexShrink: 0,
        color: 'inherit',
    },
    activeItem: {
        borderBottom: '1px solid rgba(255, 255, 255, 0.3)',
        color: theme.palette.primary.main,
        '& .MuiListItemText-primary': {
            fontFamily: PUBLIC_SANS_FONT_FAMILY,
            fontWeight: 700,
            fontSize: '15px',
        },
        '& .MuiListItemIcon-root': {
            color: theme.palette.primary.main,
        },
    },
}))
interface Props {
    initiallyOpen?: boolean;
    title: string | JSX.Element
    icon: string | JSX.Element
    isOpen?: boolean
    onToggle?: () => void
}
const MenuListItemContainer: React.FC<Props> = ({ title, icon, children, initiallyOpen, isOpen, onToggle }) => {
    const classes = useStyles()
    const [expanded, setExpanded] = React.useState(false)
    const sideMenuExpanded = useSelector((state: IRootReduxState) => state.app.state.sideMenuExpanded)
    const drawerCollapsed = !sideMenuExpanded

    React.useEffect(() => {
        if (initiallyOpen && isOpen === undefined) {
            setExpanded(true);
        }
    }, [initiallyOpen, isOpen]);

    const isExpanded = isOpen !== undefined ? isOpen : expanded;

    const handleClick = () => {
        if (onToggle) {
            onToggle();
        } else {
            setExpanded(!expanded);
        }
    };
    return (
        <>
            <Tooltip disableFocusListener={sideMenuExpanded} disableHoverListener={sideMenuExpanded} disableTouchListener={sideMenuExpanded} title={title} arrow placement="right">
                <ListItem
                    button
                    onClick={handleClick}
                    className={clsx(classes.menuListItem, classes.parentListItem, {
                        [classes.activeItem]: isExpanded,
                    })}
                >
                    <ListItemIcon className={classes.parentMenuIcon}>
                        <Icon icon={icon} />
                    </ListItemIcon>
                    {!drawerCollapsed && (
                        <>
                            <ListItemText primary={title} className={classes.parentListItemTextRoot} />
                            <div className={classes.expandIcon}>
                                {isExpanded ? <ExpandLess /> : <ExpandMore />}
                            </div>
                        </>
                    )}
                </ListItem>
            </Tooltip>
            <Collapse
                in={isExpanded}
                timeout="auto"
                unmountOnExit
                className={drawerCollapsed ? classes.collapseDrawerCollapsed : undefined}
            >
                <Box className={clsx(classes.nested, drawerCollapsed && classes.nestedCollapsedDrawer)}>
                    {children}
                </Box>
            </Collapse>
        </>
    )
}

export default MenuListItemContainer
